import { ReactElement, useEffect } from "react";
import {
  Box,
  ClickAwayListener,
  FormHelperText,
  Grow,
  Paper,
  Popper,
  Typography,
} from "@mui/material";
import { KeyboardHints } from "~/components/KeyboardHints/KeyboardHints";
import {
  AutocompleteField,
  Props as AutocompleteProps,
} from "~/components/Form/components/SelectMenu/AutocompleteField";
import { HelperText } from "~/pages/integrations/utils/integrationValidationUtils";

type BaseSelectMenuProps = {
  isOpen: boolean;
  onClose: () => void;
  anchorRef: HTMLElement | null;
  showKeyboardHint?: boolean;
  renderContent?: () => ReactElement;
  autocomplete?: AutocompleteProps;
  error?: boolean;
  helperText?: string;
};

export function BaseSelectMenu({
  onClose,
  isOpen,
  anchorRef,
  showKeyboardHint,
  renderContent,
  autocomplete,
  error,
  helperText,
}: BaseSelectMenuProps) {
  const handleEscapePress = (event: KeyboardEvent) => {
    if (event.key === "Escape") {
      onClose();
    }
  };

  useEffect(() => {
    window.addEventListener("keydown", handleEscapePress);

    return () => {
      window.removeEventListener("keydown", handleEscapePress);
    };
  }, []);

  return (
    <Popper
      sx={{
        zIndex: (theme) => theme.zIndex.modal + 1,
        minWidth: "100%",
        "& > .MuiPaper-root": {
          maxWidth: "fit-content",
        },
      }}
      open={isOpen}
      anchorEl={anchorRef}
      placement="bottom-start"
      transition
      disablePortal
      modifiers={[{ name: "flip", enabled: false }]}
    >
      {({ TransitionProps }) => (
        <Grow {...TransitionProps} style={{ transformOrigin: "left top" }}>
          <Paper
            sx={{
              border: "1px solid",
              borderColor: "primary.main",
              borderTopLeftRadius: 0,
              mt: "-1px",
              backgroundColor: "background.default",
              "& .MuiPopper-root": {
                zIndex: 100,
              },
            }}
          >
            <ClickAwayListener onClickAway={onClose}>
              <Box sx={{ minWidth: "100px", zIndex: 2 }}>
                {autocomplete && <AutocompleteField {...autocomplete} />}

                {renderContent?.()}
                {error && (
                  <FormHelperText error sx={{ ml: 3 }} color="error">
                    {helperText}
                  </FormHelperText>
                )}
                {showKeyboardHint && <KeyboardHints />}
              </Box>
            </ClickAwayListener>
          </Paper>
        </Grow>
      )}
    </Popper>
  );
}
